import React, { useState } from 'react';
import { Button, DialogFooter, Input, Label } from '@roger/ui-components';
import { useToast } from '../../hooks/useToastContext';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import {
  MIN_ESIGN_PASSWORD_LENGTH,
  completeESignatureCredentialReset,
  requestESignatureCredentialReset,
} from './esignatureCredentialApi';
import { invalidateESignatureCredentialStatus } from './useESignatureCredential';

interface ESignatureResetFieldsProps {
  /** Shown so the clinician knows which inbox to check. */
  signerEmail: string | null;
  onDone: () => void | Promise<void>;
  onCancel: () => void;
}

/**
 * Forgot-my-e-signature-password flow: email a code, then set a new password.
 *
 * A code typed back into the app rather than a link, so the clinician never
 * leaves the chart they were part-way through signing, and because the reset
 * endpoint is authenticated (it needs a session to attribute the write).
 */
export const ESignatureResetFields: React.FC<ESignatureResetFieldsProps> = ({
  signerEmail,
  onDone,
  onCancel,
}) => {
  const { success: showSuccess, error: showError } = useToast();
  const [codeSent, setCodeSent] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSendCode = async () => {
    setIsSending(true);
    try {
      await requestESignatureCredentialReset();
      setCodeSent(true);
      showSuccess('Reset code sent', 'Check your email for the code.');
    } catch (error) {
      console.error('Failed to request e-signature reset code', error);
      showError(
        'Could not send a reset code',
        getBusinessErrorMessage(error) ?? 'Please try again.'
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setValidationError('Those passwords do not match.');
      return;
    }
    if (newPassword.length < MIN_ESIGN_PASSWORD_LENGTH) {
      setValidationError(
        `Use at least ${MIN_ESIGN_PASSWORD_LENGTH} characters for your e-signature password.`
      );
      return;
    }
    setValidationError(null);
    setIsSubmitting(true);
    try {
      await completeESignatureCredentialReset(code, newPassword);
      invalidateESignatureCredentialStatus();
      showSuccess('E-signature password updated');
      await onDone();
    } catch (error) {
      console.error('Failed to reset e-signature password', error);
      showError(
        'Could not reset your e-signature password',
        getBusinessErrorMessage(error) ?? 'Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!codeSent) {
    return (
      <div className='space-y-4'>
        <p className='text-sm text-muted-foreground'>
          {signerEmail
            ? `We will email a reset code to ${signerEmail}. It expires in 30 minutes.`
            : 'We will email you a reset code. It expires in 30 minutes.'}
        </p>
        <DialogFooter>
          <Button type='button' variant='outline' onClick={onCancel}>
            Back
          </Button>
          <Button type='button' onClick={handleSendCode} disabled={isSending}>
            {isSending ? 'Sending…' : 'Email me a code'}
          </Button>
        </DialogFooter>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className='space-y-4'>
      <p className='text-sm text-muted-foreground'>
        Enter the code from your email and choose a new e-signature password.
      </p>
      <div className='space-y-1.5'>
        <Label htmlFor='esign-reset-code'>Reset code</Label>
        <Input
          id='esign-reset-code'
          name='esign-reset-code'
          type='text'
          autoComplete='one-time-code'
          autoFocus
          value={code}
          onChange={e => setCode(e.target.value)}
        />
      </div>
      {/* Hidden-but-real username field so the password manager files the new
          password under the same account as the old one. */}
      <input
        type='text'
        name='username'
        autoComplete='username'
        readOnly
        value={signerEmail ?? ''}
        className='sr-only'
        tabIndex={-1}
        aria-hidden='true'
      />
      <div className='space-y-1.5'>
        <Label htmlFor='esign-reset-new-password'>
          New e-signature password
        </Label>
        <Input
          id='esign-reset-new-password'
          name='new-password'
          type='password'
          autoComplete='new-password'
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
        />
      </div>
      <div className='space-y-1.5'>
        <Label htmlFor='esign-reset-confirm-password'>
          Confirm e-signature password
        </Label>
        <Input
          id='esign-reset-confirm-password'
          name='confirm-new-password'
          type='password'
          autoComplete='new-password'
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
        />
      </div>
      {validationError ? (
        <p className='text-sm text-destructive'>{validationError}</p>
      ) : null}
      <DialogFooter>
        <Button type='button' variant='outline' onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type='submit'
          disabled={
            isSubmitting ||
            code.length === 0 ||
            newPassword.length === 0 ||
            confirmPassword.length === 0
          }
        >
          {isSubmitting ? 'Saving…' : 'Set new password'}
        </Button>
      </DialogFooter>
    </form>
  );
};
